import _ from "lodash";
import React, { useRef, useCallback, useEffect, memo } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import { Detector } from "react-detect-offline";

import classNames from "classnames";
import {
  checkScrollAtBottom,
  prepareLoadMoreFeed,
  isUserTypeStaff,
  getGlobalTimezone,
} from "Utils";
import { compose, graphql } from "lib/apolloClient";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import Loading from "UIComponents/Loading";
import { useI18n } from "Hooks";
import {
  getDefaultNameSortPreference,
  getDefaultStudentNameOrderPreference,
  getIsParent,
  getIsStaff,
} from "modules/CommonSelectors";
import {
  getFormattedNameForUserType,
  getSubText,
  getMessageTimeSinceText,
  getChatAndChannelListQueryVariables,
  CHAT_TYPES,
  MESSAGING_FILTERS,
  CHAT_TYPE_ENUM,
  isMessagingPublicChannelEnabled,
  MESSAGING_FILTERS_CONSTANTS,
} from "MessagingV2/modules/Utils";
import { withChatOptions } from "MessagingV2/providers/ChatOptionsProvider";
import { getMessagingV2ReduxState } from "MessagingV2/modules/MessagingModule";
import { Tooltip } from "@toddle-edu/ds-web";

import Thread from "../Thread/Thread";
import EmptyChatList, { EMPTY_CHAT_LIST_VARIANTS } from "../EmptyChatList";
import classes from "./ChatAndChannelList.scss";
import { getChatAndChannelListQuery } from "../Utils/ThreadListQueries";
import ChatSuggestionsThread from "../ChatSuggestionsThread";
import { GlobeOutlined, LockOutlined } from "@toddle-edu/ds-icons";
import ThreadListSkeletonLoader from "../ThreadListSkeletonLoader";
import ConditionalWrapper from "Utils/ConditionalWrapper";

const SubTextWithIcon = props => {
  const { t } = useI18n(["common"]);

  const {
    icon: IconComponent,
    count,
    isUserMember = true,
    iconTooltip,
  } = props;

  return (
    <div className={classes.subtitleContainer}>
      <div className="text-body-s">
        {isUserMember
          ? t("common:count_members", { count })
          : t("common:not_a_member")}
      </div>
      <div className={classes.circularDivider} />
      <ConditionalWrapper
        condition={!!iconTooltip}
        wrapper={<Tooltip tooltip={iconTooltip} placement="right" />}
        type="wrapperAsElement"
      >
        <IconComponent size="xxxx-small" className={classes.icon} />
      </ConditionalWrapper>
    </div>
  );
};

export const getSubTextComponent = ({
  totalUsersCount,
  t,
  type,
  isUserMember,
}) => {
  const isPublicChannelEnabled = isMessagingPublicChannelEnabled();

  switch (type) {
    case CHAT_TYPE_ENUM.CHANNEL_CUSTOM:
      return isPublicChannelEnabled ? (
        <SubTextWithIcon
          icon={LockOutlined}
          count={totalUsersCount}
          iconTooltip={t("common:private_channel")}
        />
      ) : (
        <div className={classNames("text-body-s", classes.subtitleContainer)}>
          {t("common:count_members", { count: totalUsersCount })}
        </div>
      );

    case CHAT_TYPE_ENUM.CHANNEL_PUBLIC:
      return isPublicChannelEnabled ? (
        <SubTextWithIcon
          icon={GlobeOutlined}
          count={totalUsersCount}
          isUserMember={isUserMember}
          iconTooltip={t("common:public_channel")}
        />
      ) : (
        <div className={classNames("text-body-s", classes.subtitleContainer)}>
          {t("common:count_members", { count: totalUsersCount })}
        </div>
      );

    case CHAT_TYPE_ENUM.CHANNEL_FAMILY:
    case CHAT_TYPE_ENUM.CHANNEL_SYSTEM_MANAGED:
    default:
      return (
        <div className={classNames("text-body-s", classes.subtitleContainer)}>
          {t("common:count_members", { count: totalUsersCount })}
        </div>
      );
  }
};

function getEmptyChatListVariant({
  showSearchScreen,
  searchText,
  threadFilter,
}) {
  const isPublicTab = _.isEqual(
    threadFilter,
    MESSAGING_FILTERS_CONSTANTS.PUBLIC_CHANNEL
  );

  const isUnReadTab = _.isEqual(
    threadFilter,
    MESSAGING_FILTERS_CONSTANTS.UNREAD
  );

  const isDepartmentChannel = _.isEqual(
    threadFilter,
    MESSAGING_FILTERS_CONSTANTS.DEPARTMENT_CHANNEL
  );

  if (showSearchScreen) {
    return searchText
      ? EMPTY_CHAT_LIST_VARIANTS.SEARCH
      : EMPTY_CHAT_LIST_VARIANTS.SEARCH_WITHOUT_INPUT;
  }

  if (isPublicTab) {
    return EMPTY_CHAT_LIST_VARIANTS.PUBLIC_CHANNEL;
  }

  if (isDepartmentChannel && getIsParent()) {
    return EMPTY_CHAT_LIST_VARIANTS.DEPARTMENT_CHANNEL;
  }

  if (isDepartmentChannel && getIsStaff()) {
    return EMPTY_CHAT_LIST_VARIANTS.DEPARTMENT_CHANNEL_STAFF;
  }

  if (isUnReadTab) {
    return EMPTY_CHAT_LIST_VARIANTS.UNREAD;
  }

  return EMPTY_CHAT_LIST_VARIANTS.NO_FILTERS;
}

const ChatList = props => {
  const {
    defaultStudentNameSortPreference,
    defaultStudentNameOrderPreference,
    chatId,
    threads,
    onThreadClick,
    userType,
    refetchChatList,
    loadMoreChats,
    hasNextPage,
    getChatList,
    userTypeFilters,
    showFilterTitle,
    showSuggestions,
    threadFilter,
    showSearchScreen,
    searchFilter: searchText,
    isLoading,
    isData,
    testId,
  } = props;

  const componentTestId = testId ? `${testId}-chatList` : "messaging-chatList";
  const containerRef = useRef();

  const handleScroll = () => {
    if (
      hasNextPage &&
      getChatList?.networkStatus !== 3 &&
      containerRef.current &&
      checkScrollAtBottom(containerRef.current)
    ) {
      // get next list of chats
      loadMoreChats();
    }
  };
  const { t } = useI18n(["common"]);
  const handleNetworkChange = isOnline => {
    if (isOnline && _.isFunction(refetchChatList)) refetchChatList();
  };

  const onThreadClickHandler = useCallback(id => onThreadClick(id), [
    onThreadClick,
  ]);

  const filterTitle = _.get(MESSAGING_FILTERS, [threadFilter, "threadLabel"]);

  // scroll to top if thread filter change
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  }, [threadFilter]);

  const showThreadList =
    _.size(threads) > 0 ||
    (showSearchScreen && !_.isEmpty(searchText) && _.size(threads) > 0);

  if (isLoading && !isData) {
    return <ThreadListSkeletonLoader />;
  }

  return (
    <>
      <Detector
        polling={false}
        render={() => null}
        onChange={handleNetworkChange}
      />

      <div
        className={classes.chatMessagesContainer}
        ref={containerRef}
        onScroll={handleScroll}
      >
        {showSuggestions && !showSearchScreen ? (
          <ChatSuggestionsThread onThreadClick={onThreadClickHandler} />
        ) : null}
        {showFilterTitle && !showSearchScreen && filterTitle ? (
          <div className={classNames(classes.filterTitle, "text-body")}>
            {t(filterTitle)}
          </div>
        ) : null}

        {showThreadList ? (
          _.map(
            threads,
            ({
              id,
              isMuted,
              lastActiveAt,
              isUserMember,
              unreadMessageCount,
              type,
              label,
              isArchived,
              profileImageData = {},
              personalChatReceiverUser: messagedUser = {},
              totalUsersCount,
            }) => {
              const isPersonalChat = _.isEqual(type, CHAT_TYPES.CHAT_PERSONAL);

              const userId = _.get(messagedUser, "id");
              const profileImage = _.get(messagedUser, "profileImage", "");

              const mainText = isPersonalChat
                ? getFormattedNameForUserType({
                    user: messagedUser,
                    orderBy: defaultStudentNameSortPreference,
                    defaultStudentNameOrderPreference,
                    isFormattedNameV2: true,
                  })
                : label;

              const subText = isPersonalChat
                ? getSubText({
                    loggedInUserRole: userType,
                    messagedUserObj: messagedUser,
                    messagedUserRole: _.get(messagedUser, "type"),
                    t,
                    defaultStudentNameSortPreference,
                  })
                : null;
              const subTextComponent = !isPersonalChat
                ? getSubTextComponent({
                    totalUsersCount,
                    t,
                    type,
                    isUserMember,
                  })
                : null;

              const avatarId = isPersonalChat ? userId : id;

              return (
                <Thread
                  id={id}
                  avatarId={avatarId}
                  key={id}
                  chatType={type}
                  isMainTextHighlighted={unreadMessageCount > 0 && !isMuted}
                  mainText={mainText}
                  subText={subText}
                  subTextComponent={subTextComponent}
                  unreadMessageCount={unreadMessageCount}
                  timeSinceLastMessage={getMessageTimeSinceText(
                    lastActiveAt,
                    t
                  )}
                  isChatMuted={isMuted}
                  selected={chatId == id}
                  isArchived={
                    isPersonalChat
                      ? _.get(messagedUser, "isArchived")
                      : isArchived
                  }
                  onlineStatus={false}
                  onThreadClick={onThreadClickHandler}
                  profileIcon={profileImageData?.icon}
                  profileImage={
                    isPersonalChat ? profileImage : profileImageData?.url
                  }
                  userId={userId}
                  alertTypes={
                    messagedUser?.type === "student"
                      ? _.get(messagedUser, "alertTypes", [])
                      : undefined
                  }
                  testId={componentTestId}
                />
              );
            }
          )
        ) : (
          <EmptyChatList
            variant={getEmptyChatListVariant({
              showSearchScreen,
              searchText,
              threadFilter,
            })}
            showSearchScreen={showSearchScreen}
            searchText={searchText}
            userTypeFilters={userTypeFilters}
          />
        )}
        {getChatList?.networkStatus == 3 ? (
          <div className={classes.loadingContainer}>
            <Loading />
          </div>
        ) : null}
      </div>
    </>
  );
};

const ChatListWithData = compose(
  graphql(getChatAndChannelListQuery, {
    name: "getChatList",
    options: ({ threadFilter, searchFilter }) => ({
      fetchPolicy: "cache-and-network",
      variables: getChatAndChannelListQueryVariables({
        threadFilter,
        searchFilter,
      }),
    }),
    skip: ({ showSearchScreen, searchFilter }) =>
      showSearchScreen && _.isEmpty(searchFilter),
    props({ getChatList }) {
      const { variables, node = {} } = getChatList;

      const pageInfo = _.get(getChatList, "node.chats.pageInfo", {});
      const hasNextPage = _.get(
        getChatList,
        "node.chats.pageInfo.hasNextPage",
        false
      );

      const threads = _.map(_.get(getChatList, "node.chats.edges", []), chat =>
        _.get(chat, "node")
      );

      return {
        isData: !_.isEmpty(node),
        isLoading: _.includes([1, 2], getChatList["networkStatus"]),
        threads,
        hasNextPage,
        getChatList,
        refetchChatList: getChatList.refetch,
        loadMoreChats: prepareLoadMoreFeed({
          variables: {
            ...variables,
            input: {
              ...variables.input,
              after: _.get(pageInfo, "endCursor", undefined),
            },
          },
          queryData: getChatList,
          key: "chats",
        }),
      };
    },
  })
)(ChatList);

ChatListWithData.propTypes = {
  onThreadClick: PropTypes.func,
};

const mapStateToProps = (state, ownProps) => {
  const userInfo = getUserInfo();
  const userId = _.get(ownProps, "activeUserId");
  const userType = userInfo.user_type;
  const defaultStudentNameSortPreference = getDefaultNameSortPreference(state);
  const defaultStudentNameOrderPreference = getDefaultStudentNameOrderPreference(
    state
  );
  const isUserStaff = isUserTypeStaff({ userType });

  const messagingState = getMessagingV2ReduxState(state);

  const { threadFilter, searchFilter, isSearchFocused } = _.get(
    messagingState,
    "messageRevampV2FilterState",
    {}
  );

  const showSearchScreen = isSearchFocused || !_.isEmpty(searchFilter);

  return {
    userId,
    organizationId: userInfo.org_id,
    isUserStaff,
    userType,
    defaultStudentNameSortPreference,
    defaultStudentNameOrderPreference,
    currentTimezone: getGlobalTimezone(),
    threadFilter,
    searchFilter,
    showSearchScreen,
    isData: true,
    isLoading: false,
  };
};

export default compose(
  withRouter,
  withChatOptions(),
  connect(mapStateToProps),
  memo
)(ChatListWithData);
